<script lang="ts">
  import { onDestroy } from 'svelte';

  import {
    getAISuggestionPrompt,
    type AISuggestionPrompt,
    type AISuggestionPromptId,
  } from '@/teoCore/text/AIHandler/aiSuggestionPresets';
  import { type AISuggestion } from '@/teoCore/text/AIHandler/SelectionSuggestionHandler';
  import type { TeoText } from '@/teoCore/text/TeoText';
  import AiCard, {
    type AICardState,
  } from '@/ui/components/assistant/Performance/AICard/AICard.svelte';
  import type { DropDownItem } from '@/ui/elements/dropdown/DropDown/DropDownSelect.svelte';
  import { useTeoEventObserver } from '@/utils/use-teo-event-observer.svelte';

  const { teoText, withAutoMode = false }: { teoText: TeoText; withAutoMode?: boolean } = $props();

  let aiCardState: AICardState = $state('default');
  let aiPromptItems: DropDownItem<AISuggestionPromptId>[] | undefined = $state();
  let currentPrompt: AISuggestionPrompt | undefined = $state();
  let aiSuggestions = useTeoEventObserver({
    initialValue: teoText.ai.selection.activeSuggestions,
    update: () => teoText.ai.selection.activeSuggestions,
    target: teoText.ai.selection.events,
    events: ['aiSuggestionReceived', 'modeChanged', 'suggestionRemoved'],
  });

  const unsubFromAIEvents = teoText.ai.selection.events.addEventListeners({
    loading: updateCardState,
    modeChanged: updateCardState,
  });

  const unsubFromAnalysisEvents = teoText.analysis?.events.addEventListener(
    'currentInstructionChanged',
    () => teoText.ai.selection.disable(),
  );

  onDestroy(() => {
    unsubFromAIEvents();
    unsubFromAnalysisEvents?.();
  });

  function handleModeSelection(id: 'auto' | 'manual'): void {
    if (id === 'auto') {
      const auto = teoText.ai.auto;
      auto.history.ensureSessionBaseline();
      const latestTpi = auto.store.getLatestHistoryEntry()?.tpi ?? 0;
      const stale = !teoText.analysisState.isCurrent;
      teoText.store.setStateValue('performanceMode', 'autoAI');
      if (auto.canRun && (latestTpi < auto.getTpiTarget() || stale)) {
        void auto.startLoop();
      }
      return;
    }
    const prompts = teoText.ai.selection.updatePromptList();
    const initialPrompt = prompts[0];
    if (!initialPrompt) {
      // TODO: aiCardState for no prompts
      console.warn('No AI prompts available');
      // aiCardState = 'default';
      updateCardState();
      return;
    }
    onPromptSelect(initialPrompt.id);
    aiPromptItems = prompts.map((item) => ({
      label: item.title,
      value: item.id,
      disabled: !item.available,
    }));

    // aiCardState = 'result';
    updateCardState();
  }

  function onPromptSelect(item: AISuggestionPromptId): void {
    const preset = getAISuggestionPrompt(item);
    if (preset) {
      currentPrompt = preset;
      teoText.ai.selection.enable(currentPrompt);
    }
  }

  function updateCardState(): void {
    if (teoText.ai.selection.isLoading) {
      aiCardState = 'loading';
    } else if (teoText.ai.selection.isEnabled) {
      aiCardState = 'result';
    } else {
      aiCardState = 'default';
    }
  }

  function onUnloadClick(): void {
    teoText.ai.selection.disable();
  }

  function onRemoveSuggestion(index: number): void {
    teoText.ai.selection.removeSuggestion(index);
  }

  function onInsertClick(aiSuggestion?: AISuggestion): void {
    if (!aiSuggestion) {
      console.warn('No AI suggestion provided');
      return;
    }
    teoText.ai.useSuggestion($state.snapshot(aiSuggestion));
  }

  function handleLoadSuggestion(modifier: string): void {
    teoText.ai.selection.resendActive(modifier);
  }
</script>

<AiCard
  cardLabel="KI-Optimierung"
  cardLoadLabel="Starten"
  instructionText={currentPrompt?.description}
  aiStyleTooltip="neu generieren"
  aiStyleLabel="KI-Stil"
  promptItems={aiPromptItems}
  promptItemSelected={currentPrompt?.id}
  aiSuggestions={aiSuggestions.current}
  onLoadSuggestion={handleLoadSuggestion}
  state={aiCardState}
  {onUnloadClick}
  {onInsertClick}
  {onRemoveSuggestion}
  onAIStyleClick={handleLoadSuggestion}
  {onPromptSelect}
  headerAction={withAutoMode ?
    {
      actions: [
        {
          id: 'manual',
          label: 'KI-Tools',
          onclick: () => handleModeSelection('manual'),
          disabled: false,
          icon: 'MagicButton',
        },
        {
          id: 'auto',
          label: 'KI-Autopilot',
          onclick: () => handleModeSelection('auto'),
          disabled: false,
          icon: 'MagicExchange',
        },
      ],
      label: 'Starten',
    }
  : {
      label: 'Starten',
      variant: 'primary',
      onclick: () => handleModeSelection('manual'),
    }}
></AiCard>
